import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Drawer,
    Stack,
    Switch,
    Typography,
} from "@mui/material";
import {
    AddRounded,
    DeleteOutlineRounded,
    HistoryRounded,
    KeyboardArrowDownRounded,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import { Button, IconButton } from "../components/Buttons";
import {
    deleteWorkflow,
    listWorkflowRuns,
    listWorkflows,
    toggleWorkflow,
} from "../api";
import { formatDateTime } from "../utils/format";
import { apiError, triggerLabel } from "../utils/meta";

const RUN_CHIP = {
    matched: { label: "Matched", color: "var(--success)" },
    skipped: { label: "Skipped", color: "var(--text-tertiary)" },
    error: { label: "Error", color: "var(--error)" },
};

/* ------------------------------------------------------------------ */
/* Runs drawer                                                          */
/* ------------------------------------------------------------------ */

function RunsDrawer({ workflow, onClose }) {
    const open = !!workflow;
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(null);

    useEffect(() => {
        if (!workflow) return;
        setExpanded(null);
        (async () => {
            setLoading(true);
            try {
                const { data } = await listWorkflowRuns(workflow.id);
                setRuns(Array.isArray(data) ? data : (data?.data ?? []));
            } catch (err) {
                toast.error(apiError(err, "Failed to load runs"));
            } finally {
                setLoading(false);
            }
        })();
    }, [workflow]);

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: { width: { xs: "100%", md: 520 }, background: "var(--bg)" },
            }}
        >
            <Stack sx={{ height: "100%" }}>
                <Stack
                    sx={{
                        p: "24px 28px 20px",
                        borderBottom: "1px solid var(--border)",
                        gap: 0.5,
                        background: "var(--bg-secondary)",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 11,
                            letterSpacing: "2px",
                            fontWeight: 700,
                            color: "var(--text-tertiary)",
                        }}
                    >
                        RECENT RUNS
                    </Typography>
                    <Typography variant="h5">{workflow?.name}</Typography>
                </Stack>

                <Stack sx={{ flex: 1, overflowY: "auto", p: "20px 28px", gap: 1.25 }}>
                    {loading && (
                        <Stack sx={{ alignItems: "center", py: 6 }}>
                            <CircularProgress size={26} />
                        </Stack>
                    )}
                    {!loading && runs.length === 0 && (
                        <Typography
                            sx={{
                                fontSize: 14,
                                color: "var(--text-secondary)",
                                textAlign: "center",
                                py: 6,
                            }}
                        >
                            No runs yet — this workflow hasn't fired.
                        </Typography>
                    )}
                    {!loading &&
                        runs.map((run) => {
                            const chip =
                                RUN_CHIP[run.status] ?? RUN_CHIP.skipped;
                            const isOpen = expanded === run.id;
                            return (
                                <Stack
                                    key={run.id}
                                    sx={{
                                        border: "1px solid var(--border-mid)",
                                        borderRadius: "4px",
                                        background: "var(--bg-secondary)",
                                    }}
                                >
                                    <Stack
                                        onClick={() =>
                                            setExpanded(
                                                isOpen ? null : run.id,
                                            )
                                        }
                                        sx={{
                                            flexDirection: "row",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            p: "12px 14px",
                                            cursor: "pointer",
                                        }}
                                    >
                                        <Stack
                                            sx={{
                                                flexDirection: "row",
                                                alignItems: "center",
                                                gap: 1.5,
                                            }}
                                        >
                                            <Chip
                                                size="small"
                                                label={chip.label}
                                                sx={{
                                                    height: 22,
                                                    fontSize: 11.5,
                                                    fontWeight: 600,
                                                    color: chip.color,
                                                    background: "transparent",
                                                    border: `1px solid ${chip.color}`,
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontSize: 13,
                                                    color: "var(--text-secondary)",
                                                }}
                                            >
                                                {formatDateTime(run.createdAt)}
                                            </Typography>
                                        </Stack>
                                        <KeyboardArrowDownRounded
                                            sx={{
                                                fontSize: 18,
                                                color: "var(--text-tertiary)",
                                                transform: isOpen
                                                    ? "rotate(180deg)"
                                                    : "none",
                                                transition: "transform 0.15s",
                                            }}
                                        />
                                    </Stack>
                                    <Collapse in={isOpen}>
                                        <Stack
                                            sx={{
                                                borderTop:
                                                    "1px solid var(--border)",
                                                p: "12px 14px",
                                                gap: 0.75,
                                            }}
                                        >
                                            {run.recordId && (
                                                <Typography
                                                    sx={{
                                                        fontSize: 12,
                                                        color: "var(--text-tertiary)",
                                                        fontFamily:
                                                            "monospace",
                                                    }}
                                                >
                                                    record {run.recordId}
                                                </Typography>
                                            )}
                                            <Typography
                                                component="pre"
                                                sx={{
                                                    m: 0,
                                                    fontSize: 12,
                                                    fontFamily: "monospace",
                                                    whiteSpace: "pre-wrap",
                                                    wordBreak: "break-word",
                                                    color: run.error
                                                        ? "var(--error)"
                                                        : "var(--text-secondary)",
                                                }}
                                            >
                                                {run.error ||
                                                    (typeof run.log ===
                                                    "string"
                                                        ? run.log
                                                        : JSON.stringify(
                                                              run.log,
                                                              null,
                                                              2,
                                                          )) ||
                                                    "No log output"}
                                            </Typography>
                                        </Stack>
                                    </Collapse>
                                </Stack>
                            );
                        })}
                </Stack>
            </Stack>
        </Drawer>
    );
}

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */

export default function AdminWorkflows() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [runsFor, setRunsFor] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [deleteBusy, setDeleteBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await listWorkflows();
            setRows(Array.isArray(data) ? data : (data?.data ?? []));
        } catch (err) {
            toast.error(apiError(err, "Failed to load workflows"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    async function handleToggle(row) {
        /* Optimistic flip, revert on failure. */
        setRows((prev) =>
            prev.map((r) =>
                r.id === row.id ? { ...r, isActive: !r.isActive } : r,
            ),
        );
        try {
            await toggleWorkflow(row.id);
        } catch (err) {
            setRows((prev) =>
                prev.map((r) =>
                    r.id === row.id ? { ...r, isActive: row.isActive } : r,
                ),
            );
            toast.error(apiError(err, "Could not toggle the workflow"));
        }
    }

    async function handleDelete() {
        if (!deleting) return;
        setDeleteBusy(true);
        try {
            await deleteWorkflow(deleting.id);
            toast.success(`Workflow "${deleting.name}" deleted`);
            setDeleting(null);
            load();
        } catch (err) {
            toast.error(apiError(err, "Could not delete the workflow"));
        } finally {
            setDeleteBusy(false);
        }
    }

    const columns = useMemo(
        () => [
            {
                key: "name",
                label: "Workflow",
                render: (row) => (
                    <Stack sx={{ gap: 0.25 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                            {row.name}
                        </Typography>
                        {row.description && (
                            <Typography
                                sx={{
                                    fontSize: 12.5,
                                    color: "var(--text-secondary)",
                                }}
                            >
                                {row.description}
                            </Typography>
                        )}
                    </Stack>
                ),
            },
            {
                key: "objectName",
                label: "Object",
                render: (row) => row.objectName ?? "—",
            },
            {
                key: "trigger",
                label: "Trigger",
                render: (row) => triggerLabel(row.trigger),
            },
            {
                key: "isActive",
                label: "Active",
                render: (row) => (
                    <Switch
                        size="small"
                        checked={!!row.isActive}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => handleToggle(row)}
                    />
                ),
            },
            {
                key: "createdAt",
                label: "Last edited",
                render: (row) =>
                    formatDateTime(row.updatedAt ?? row.createdAt),
            },
            {
                key: "actions",
                label: "",
                align: "right",
                render: (row) => (
                    <Stack
                        sx={{
                            flexDirection: "row",
                            justifyContent: "flex-end",
                            gap: 0.5,
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <IconButton
                            size="32px"
                            variant="text"
                            title="Runs"
                            onClick={() => setRunsFor(row)}
                        >
                            <HistoryRounded sx={{ fontSize: 18 }} />
                        </IconButton>
                        <IconButton
                            size="32px"
                            variant="text"
                            color="error"
                            title="Delete"
                            onClick={() => setDeleting(row)}
                        >
                            <DeleteOutlineRounded sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Stack>
                ),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    return (
        <Stack>
            <PageHeader
                title="Workflows"
                subtitle="Automate what happens when your records change — as many as you need"
                action={
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddRounded />}
                        onClick={() => navigate("/admin/workflows/new")}
                    >
                        New workflow
                    </Button>
                }
            />

            <DataTable
                columns={columns}
                rows={rows}
                loading={loading}
                emptyMessage="No workflows yet — create the first one"
                onRowClick={(row) => navigate(`/admin/workflows/${row.id}`)}
            />

            <RunsDrawer workflow={runsFor} onClose={() => setRunsFor(null)} />

            <Dialog open={!!deleting} onClose={() => setDeleting(null)}>
                <DialogTitle
                    sx={{ fontFamily: "'Newsreader', Georgia, serif" }}
                >
                    Delete this workflow?
                </DialogTitle>
                <DialogContent>
                    <Typography
                        sx={{ fontSize: 14.5, color: "var(--text-secondary)" }}
                    >
                        &#8220;{deleting?.name}&#8221; will stop running and be
                        removed permanently.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button
                        variant="soft-outlined"
                        onClick={() => setDeleting(null)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        loading={deleteBusy}
                        onClick={handleDelete}
                    >
                        Delete workflow
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
